import type { AxiosError } from 'axios';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Phone,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';
import { getApiErrorMessage } from '@/lib/api-errors';
import {
  PHONE_INPUT_MAX_LENGTH,
  applyPhoneMask,
  getPhoneError,
  normalizePhoneDigits,
} from '@/lib/phone';

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const incomingState = (location.state as { name?: string; email?: string; billingCycle?: 'monthly' | 'yearly' }) || {};
  const incomingBillingCycle = (incomingState.billingCycle as
    | 'monthly'
    | 'yearly'
    | undefined) || 'monthly';

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: incomingState.name || '',
    email: incomingState.email || '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Novo cadastro entra automaticamente no plano padrão (Equilíbrio) com 7 dias
  // grátis — não há mais etapa de escolha de plano no signup.

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    const telefoneInvalido = getPhoneError(formData.phone);
    if (telefoneInvalido) {
      setPhoneError(telefoneInvalido);
      toast({
        title: "Telefone inválido",
        description: telefoneInvalido,
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Senhas diferentes",
        description: "As senhas não coincidem",
        variant: "destructive",
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: "Senha fraca",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: normalizePhoneDigits(formData.phone),
        password: formData.password,
        billing_cycle: incomingBillingCycle,
      });

      toast({
        title: 'Conta criada!',
        description: 'Você tem 7 dias grátis no plano Equilíbrio. Aproveite!',
      });
      // O contexto já autenticou a sessão (tokens salvos + user carregado):
      // entramos direto no app em trial, sem passar pela tela de login.
      navigate('/financial', { replace: true });
    } catch (error: unknown) {
      console.error('Erro no registro:', error);
      toast({
        title: "Erro ao criar conta",
        // Em 422 o `detail` é a lista de erros do FastAPI: sem isto o usuário
        // recebia o JSON do pydantic no lugar da mensagem.
        description: getApiErrorMessage(
          (error as AxiosError<{ detail?: string }>).response?.data?.detail,
          "Tente novamente mais tarde.",
        ),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone: applyPhoneMask(value) }));
    // Digitar sempre limpa o aviso: manter na tela um erro do estado anterior
    // ("Informe seu telefone") acusa o problema errado enquanto o número certo
    // ainda está a meio caminho. A cobrança volta no blur e no envio.
    setPhoneError(null);
  };

  const benefits = [
    'Dashboard financeiro completo',
    'Controle de receitas e despesas',
    'Despesas futuras e planejamento financeiro',
    'Relatórios inteligentes',
    'Alertas personalizados',
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Benefits */}
      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden"
        style={{ background: BRAND_HERO_GRADIENT }}
      >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          {/* Logo */}
          <div className="relative z-10">
            <img
              src="/logoSidebar.png"
              alt="Librattum"
              className="h-10"
            />
          </div>

          {/* Content */}
          <div className="relative z-10 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                Comece gratuitamente
              </div>
              <h1 className="text-4xl font-bold text-white leading-tight">
                Transforme sua<br />
                vida financeira
              </h1>
              <p className="text-white/80 text-lg max-w-md">
                Milhares de pessoas já organizam suas finanças com o Librattum.
                Junte-se a eles e comece hoje mesmo.
              </p>
            </div>

            {/* Benefits List */}
            <div className="space-y-3">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 text-white"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#F5B322]/20 flex items-center justify-center">
                    <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
                  </div>
                  <span className="text-white/90">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="p-6 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              <p className="text-white/90 italic mb-4">
                "O Librattum mudou completamente minha relação com dinheiro.
                Agora sei exatamente para onde vai cada centavo."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold">
                  MS
                </div>
                <div>
                  <p className="text-white font-medium">Maria Santos</p>
                  <p className="text-white/60 text-sm">Usuária desde 2024</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-10 text-white/60 text-sm">
            © {new Date().getFullYear()} Librattum. Todos os direitos reservados.
          </div>
        </div>

        {/* Right Section - Register Form */}
        <div className="flex-1 flex items-center justify-center p-8 brand-light-surface">
          <div className="w-full max-w-md space-y-8">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img
                src="/logo-librattum.png"
                alt="Librattum"
                className="h-10 mx-auto"
              />
            </div>

            {/* Header */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">
                Criar sua conta
              </h2>
              <p className="mt-2 text-gray-600">
                Comece a organizar suas finanças gratuitamente
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleRegister} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700">
                  Nome completo
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    placeholder="Escreva seu nome completo"
                    className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700">
                  Telefone
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="(11) 98472-6153"
                    className={`pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322] ${
                      phoneError ? 'border-red-400 focus-visible:border-red-400 focus-visible:ring-red-400' : ''
                    }`}
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onBlur={(e) => setPhoneError(getPhoneError(e.target.value))}
                    disabled={isLoading}
                    required
                    maxLength={PHONE_INPUT_MAX_LENGTH}
                    aria-invalid={phoneError ? true : undefined}
                    aria-describedby={phoneError ? 'phone-error' : undefined}
                  />
                </div>
                {phoneError && (
                  <p id="phone-error" role="alert" className="text-sm text-red-600">
                    {phoneError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700">
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mín. 6 caracteres"
                      className="pl-11 pr-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      disabled={isLoading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-gray-700">
                    Confirmar
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Repita a senha"
                      className="pl-11 h-12 bg-gray-50 border-gray-200 focus:bg-white focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Ao criar sua conta, você concorda com nossos{' '}
                <a href="/termos" className="text-[#F5B322] hover:underline">
                  Termos de Uso
                </a>{' '}
                e{' '}
                <a href="/privacidade" className="text-[#F5B322] hover:underline">
                  Política de Privacidade
                </a>.
              </p>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 border-2 border-[#2F2117]/30 border-t-[#2F2117] rounded-full animate-spin" />
                    Criando conta...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Continuar
                    <ArrowRight className="h-4 w-4" />
                  </div>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">ou</span>
              </div>
            </div>

            {/* Login Link */}
            <div className="text-center space-y-4">
              <p className="text-gray-600">
                Já tem uma conta?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-[#F5B322] hover:underline"
                >
                  Fazer login
                </Link>
              </p>

              <Button
                type="button"
                variant="outline"
                className="w-full h-12"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao Login
              </Button>
            </div>
          </div>
        </div>
      </div>
  );
}
